import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { useTranslation } from 'react-i18next';

import { messagingService } from '../../../services/messaging.service';
import { theme } from '../../../theme';

const SettingRow = ({
  title,
  description,
  checked,
  onChange,
  ariaLabel,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      borderRadius: '18px',
      border: `1px solid ${theme.colors.secondary[200]}`,
      backgroundColor: theme.colors.secondary[50],
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, color: theme.colors.secondary[950] }}>
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.35, color: theme.colors.grey[600] }}
      >
        {description}
      </Typography>
    </Box>
    <Switch
      checked={checked}
      onChange={onChange}
      inputProps={{ 'aria-label': ariaLabel }}
      sx={{
        flexShrink: 0,
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: theme.colors.primary[800],
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          backgroundColor: theme.colors.primary[600],
        },
      }}
    />
  </Box>
);

const ClientChatSettings = ({ salon }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!salon?.is_owner) return undefined;
    let active = true;
    messagingService.getClientChatSettings(salon.salon_id)
      .then((response) => active && setValues(response.data))
      .catch(() => active && setError(t('client_messaging.settings_error', 'Impossible de charger les réglages.')));
    return () => { active = false; };
  }, [salon?.is_owner, salon?.salon_id, t]);

  if (!salon?.is_owner) return null;
  if (!values) return error ? <Alert severity="error">{error}</Alert> : null;

  const update = async (patch) => {
    const next = {
      is_enabled: Boolean(patch.is_enabled ?? values.is_enabled),
      notify_outside_opening_hours: Boolean(
        patch.notify_outside_opening_hours ?? values.notify_outside_opening_hours
      ),
    };
    setValues(next);
    try {
      await messagingService.updateClientChatSettings(salon.salon_id, next);
    } catch (_requestError) {
      setError(t('client_messaging.settings_error', 'Impossible d’enregistrer les réglages.'));
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 2,
          borderRadius: '20px',
          border: `1px solid ${theme.colors.secondary[200]}`,
          background: `linear-gradient(135deg, ${theme.colors.secondary[50]}, ${theme.colors.primary[50]})`,
          boxShadow: '0 12px 30px rgba(67, 44, 23, 0.07)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 46,
                height: 46,
                flexShrink: 0,
                borderRadius: '15px',
                color: theme.colors.primary[900],
                backgroundColor: theme.colors.primary[100],
              }}
            >
              <ChatBubbleOutlineRoundedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 800, color: theme.colors.secondary[950] }}
              >
                {t('client_messaging.settings_summary', 'Messagerie clientes')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.25, color: theme.colors.grey[600] }}
              >
                {t(
                  'client_messaging.settings_summary_description',
                  'Permettez aux clientes de contacter votre salon depuis sa fiche.',
                )}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Chip
              label={
                values.is_enabled
                  ? t('client_messaging.enabled', 'Activée')
                  : t('client_messaging.disabled', 'Désactivée')
              }
              size="small"
              sx={{
                fontWeight: 800,
                color: values.is_enabled
                  ? theme.colors.primary[950]
                  : theme.colors.grey[700],
                backgroundColor: values.is_enabled
                  ? theme.colors.primary[100]
                  : theme.colors.grey[200],
              }}
            />
            <Button
              variant="contained"
              startIcon={<SettingsRoundedIcon />}
              onClick={() => setOpen(true)}
              sx={{
                px: 1.75,
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: '12px',
                backgroundColor: theme.colors.primary[800],
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.colors.primary[900],
                  boxShadow: 'none',
                },
              }}
            >
              {t('client_messaging.configure', 'Configurer')}
            </Button>
          </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </Paper>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '24px',
            backgroundColor: theme.colors.secondary[50],
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: { xs: 2, sm: 2.5 },
            py: 2,
            borderBottom: `1px solid ${theme.colors.secondary[200]}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 38,
              height: 38,
              borderRadius: '12px',
              color: theme.colors.primary[900],
              backgroundColor: theme.colors.primary[100],
            }}
          >
            <ChatBubbleOutlineRoundedIcon fontSize="small" />
          </Box>
          <Typography
            component="span"
            sx={{
              flex: 1,
              fontWeight: 800,
              color: theme.colors.secondary[950],
            }}
          >
            {t(
              'client_messaging.configure_title',
              'Configurer la messagerie',
            )}
          </Typography>
          <IconButton
            aria-label={t(
              'client_messaging.close_settings',
              'Fermer les réglages',
            )}
            onClick={() => setOpen(false)}
            sx={{ color: theme.colors.grey[700] }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'grid',
            gap: 1.25,
            px: { xs: 2, sm: 2.5 },
            pt: '20px !important',
            pb: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.colors.grey[600], mb: 0.5 }}
          >
            {t(
              'client_messaging.auto_save',
              'Les changements sont enregistrés automatiquement.',
            )}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <SettingRow
            title={t(
              'client_messaging.enable_chat_title',
              'Autoriser les messages clientes',
            )}
            description={t(
              'client_messaging.enable_chat_description',
              'Affiche le bouton de contact sur la fiche de votre salon.',
            )}
            checked={Boolean(values.is_enabled)}
            ariaLabel={t(
              'client_messaging.enable_chat',
              'Autoriser les messages clientes',
            )}
            onChange={(event) => update({
              is_enabled: event.target.checked,
            })}
          />
          <SettingRow
            title={t(
              'client_messaging.outside_hours_title',
              'Notifications hors horaires',
            )}
            description={t(
              'client_messaging.outside_hours_description',
              'Recevez aussi les alertes lorsque le salon est fermé.',
            )}
            checked={Boolean(values.notify_outside_opening_hours)}
            ariaLabel={t(
              'client_messaging.outside_hours',
              'Notifications hors horaires',
            )}
            onChange={(event) => update({
              notify_outside_opening_hours: event.target.checked,
            })}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientChatSettings;
